import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatCurrency,
  formatNumber,
  formatNumberOrUnknown,
  formatPercentage,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import { components } from "../../types/api";
import OrbLoader from "../common/OrbLoader";
import { useSelector } from "react-redux";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";

interface InsertionOrder {
  id?: string;
  name?: string;
  dailyData: components["schemas"]["DailyLineItemMetrics"][];
  summary: components["schemas"]["MetricsLineItemSummary"];
}

interface InsertionOrdersTableProps {
  insertionOrders: InsertionOrder[] | undefined;
  isLoading: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  selectedInsertionOrderId: string | undefined;
  onInsertionOrderClick: (insertionOrder: InsertionOrder) => void;
  currency?: components["schemas"]["currency"];
  format?: "DISPLAY" | "NATIVE" | "VIDEO" | "ALL" | "AUDIO";
  useTotalsForCtr?: boolean;
  variant?: "default" | "amazonCtv";
}

const InsertionOrdersTable: React.FC<InsertionOrdersTableProps> = ({
  insertionOrders,
  isLoading,
  maxHeight = 400,
  stickyHeader = true,
  selectedInsertionOrderId,
  onInsertionOrderClick,
  currency = "EUR",
  format = "ALL",
  useTotalsForCtr = false,
  variant = "default",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const navigate = useNavigate();
  const isAmazonCtvVariant = variant === "amazonCtv";
  const showVideoMetrics = format === "VIDEO" && !isAmazonCtvVariant;
  const showAudioMetrics = format === "AUDIO" && !isAmazonCtvVariant;

  const getAvgCPV = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    const value = (summary as Record<string, unknown>).avgCPV;
    return typeof value === "number" ? value : 0;
  };

  const resolveCompleteViews = (
    summary:
      | components["schemas"]["MetricsLineItemSummary"]
      | Record<string, unknown>
  ): number => {
    if (typeof summary.totalCompleteViews === "number") {
      return summary.totalCompleteViews;
    }

    const summaryRecord = summary as Record<string, unknown>;
    const videoViews = summaryRecord.totalVideoViews;
    if (typeof videoViews === "number") {
      return videoViews;
    }

    const visualizations = summaryRecord.visualizations;
    if (typeof visualizations === "number") {
      return visualizations;
    }

    const completeViews = summaryRecord.completeViews;
    if (typeof completeViews === "number") {
      return completeViews;
    }

    return 0;
  };

  const calculateVtr = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    const totalImpressions = summary.totalImpressions ?? 0;
    const totalCompleteViews = resolveCompleteViews(summary);

    if (totalImpressions === 0) {
      return formatPercentage(0);
    }

    return formatPercentage(totalCompleteViews / totalImpressions);
  };

  const calculateCtr = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    if (!useTotalsForCtr) {
      return formatPercentageNoMultiply(summary.avgCTR ?? 0);
    }

    const totalImpressions = summary.totalImpressions ?? 0;
    const totalClicks = summary.totalClicks ?? 0;
    const ctrValue = totalImpressions > 0 ? totalClicks / totalImpressions : 0;

    return formatPercentage(ctrValue);
  };

  const getIndexListenComplete = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    const totalCompleteViews = summary.totalCompleteViews ?? 0;
    const totalImpressions = summary.totalImpressions ?? 0;
    const ratio =
      totalImpressions > 0 ? totalCompleteViews / totalImpressions : 0;

    return formatPercentage(ratio);
  };

  const handleViewClick = (
    e: React.MouseEvent,
    insertionOrder: InsertionOrder
  ) => {
    e.stopPropagation();
    if (insertionOrder.id) {
      navigate(`/metrics/io-margins/${insertionOrder.id}`, {
        state: { insertionOrderName: insertionOrder.name },
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <OrbLoader
          size="large"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  if (!insertionOrders || insertionOrders.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {t("campaigns.noInsertionOrders", {
            defaultValue: "No insertion orders available for this campaign",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        maxHeight: maxHeight,
        overflow: "auto",
        mt: 3,
      }}
    >
      <Table stickyHeader={stickyHeader} size="small">
        <TableHead>
          <TableRow>
            {isAmazonCtvVariant ? (
              <>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.name", { defaultValue: "Name" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.cost", { defaultValue: "Cost" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.impressions", { defaultValue: "Impressions" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.completeViews", {
                    defaultValue: "Complete Views",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.cpm", { defaultValue: "CPM" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.vtr", { defaultValue: "VTR" })}
                </TableCell>
              </>
            ) : (
              <>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.name", { defaultValue: "Name" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.impressions", { defaultValue: "Impressions" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.clicks", { defaultValue: "Clicks" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.ctr", { defaultValue: "CTR" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.cpm", { defaultValue: "CPM" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.cpc", { defaultValue: "CPC" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.cost", { defaultValue: "Cost" })}
                </TableCell>
              </>
            )}
            {isAdmin && (
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.background.default,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  py: 1.5,
                }}
              >
                {t("campaigns.realCost", { defaultValue: "Real Cost" })}
              </TableCell>
            )}
            {!isAmazonCtvVariant && (
              <>
                {showVideoMetrics && (
                  <>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.vtr", { defaultValue: "VTR" })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.firstQuartile", {
                        defaultValue: "First Quartile",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.secondQuartile", {
                        defaultValue: "Second Quartile",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.thirdQuartile", {
                        defaultValue: "Third Quartile",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.totalConversions", {
                        defaultValue: "Conversions",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.uniqueUsers", {
                        defaultValue: "Unique Users",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.completeViews", {
                        defaultValue: "Complete Views",
                      })}
                    </TableCell>
                  </>
                )}
                {showAudioMetrics && (
                  <>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.cpv", { defaultValue: "CPV" })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.listenComplete", {
                        defaultValue: "Listen Complete",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.indexListenComplete", {
                        defaultValue: "Index Listen Complete",
                      })}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                        py: 1.5,
                      }}
                    >
                      {t("campaigns.totalReach", { defaultValue: "Reach" })}
                    </TableCell>
                  </>
                )}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.measurableImpressions", {
                    defaultValue: "Measurable Impressions",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.engagementRate", {
                    defaultValue: "Engagement Rate",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.interactionTime", {
                    defaultValue: "Interaction Time",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.totalReach", { defaultValue: "Total Reach" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                  align="center"
                >
                  {t("common.actions", { defaultValue: "Actions" })}
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {insertionOrders.map((insertionOrder) => (
            <TableRow
              key={insertionOrder.id || insertionOrder.name}
              onClick={() => onInsertionOrderClick(insertionOrder)}
              hover
              selected={selectedInsertionOrderId === insertionOrder.id}
              sx={{
                cursor: "pointer",
                borderBottom: `1px solid ${theme.palette.divider}`,
                "&:last-child td, &:last-child th": { border: 0 },
                "&.Mui-selected": {
                  backgroundColor: `${theme.palette.primary.light}20`,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: `${theme.palette.primary.light}30`,
                },
              }}
            >
              {isAmazonCtvVariant ? (
                (() => {
                  const completeViewsValue = resolveCompleteViews(
                    insertionOrder.summary
                  );
                  return (
                    <>
                      <TableCell
                        sx={{
                          fontWeight:
                            selectedInsertionOrderId === insertionOrder.id
                              ? 600
                              : 400,
                        }}
                      >
                        {insertionOrder.name || "Unnamed Insertion Order"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatCurrency(
                          insertionOrder.summary.totalCost,
                          currency
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(insertionOrder.summary.totalImpressions)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(completeViewsValue)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatCurrency(
                          insertionOrder.summary.avgCPM,
                          currency
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {calculateVtr(insertionOrder.summary)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                          {insertionOrder.summary.totalRealCost !== undefined
                            ? formatCurrency(
                                insertionOrder.summary.totalRealCost,
                                currency
                              )
                            : "-"}
                        </TableCell>
                      )}
                    </>
                  );
                })()
              ) : (
                <>
                  <TableCell
                    sx={{
                      fontWeight:
                        selectedInsertionOrderId === insertionOrder.id
                          ? 600
                          : 400,
                    }}
                  >
                    {insertionOrder.name || "Unnamed Insertion Order"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(insertionOrder.summary.totalImpressions)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(insertionOrder.summary.totalClicks)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {calculateCtr(insertionOrder.summary)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatCurrency(insertionOrder.summary.avgCPM, currency)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatCurrency(insertionOrder.summary.avgCPC, currency)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatCurrency(insertionOrder.summary.totalCost, currency)}
                  </TableCell>
                  {showVideoMetrics && (
                    <>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatPercentage(insertionOrder.summary.avgVTR || 0)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(
                          insertionOrder.summary.totalFirstQuartileViews || 0
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(
                          insertionOrder.summary.totalMidpointViews || 0
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(
                          insertionOrder.summary.totalThirdQuartileViews || 0
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(
                          insertionOrder.summary.totalConversions || 0
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumberOrUnknown(
                          insertionOrder.summary.totalUniqueUsers
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(
                          resolveCompleteViews(insertionOrder.summary)
                        )}
                      </TableCell>
                    </>
                  )}
                  {showAudioMetrics && (
                    <>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatCurrency(
                          getAvgCPV(insertionOrder.summary),
                          currency
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumber(
                          insertionOrder.summary.totalCompleteViews || 0
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {getIndexListenComplete(insertionOrder.summary)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                        {formatNumberOrUnknown(
                          insertionOrder.summary.totalUniqueUsers
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(
                      insertionOrder.summary.totalMeasurableImpressions || 0
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatPercentage(
                      insertionOrder.summary.avgEngagementRate || 0
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(
                      insertionOrder.summary.avgInteractionTime || 0
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumberOrUnknown(
                      insertionOrder.summary.totalReachCoViewed
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                      {insertionOrder.summary.totalRealCost !== undefined
                        ? formatCurrency(
                            insertionOrder.summary.totalRealCost,
                            currency
                          )
                        : "-"}
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.875rem", py: 1.5 }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => handleViewClick(e, insertionOrder)}
                      sx={{
                        color: theme.palette.info.main,
                        "&:hover": {
                          backgroundColor: theme.palette.info.light + "20",
                        },
                      }}
                      disabled={!insertionOrder.id}
                      aria-label={t("common.view", { defaultValue: "View" })}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InsertionOrdersTable;
